import React, { useState, useEffect, useRef } from "react";
import { ParticipantAvatar, matchSides } from "../ui/Matchup";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import matchService from "../../services/api/matchService";
import { getServerSetScores, getViewerVoteSide, getDoublesPartners, getCancellationInfo, canFinalizeNow } from "../../utils/matchDetailsUtils";
import teamService from "../../services/api/teamService";
import { userService } from "../../services/api/userService";
import GameLayout from "../layout/GameLayout";
import FloatingCard from "../ui/FloatingCard";
import FloatingButton from "../ui/FloatingButton";
import StatusBadge from "../ui/StatusBadge";
import ShareMatchModal from "./ShareMatchModal";
import { getSportDefaults, getSportConfigByName } from "../../config/sportsConfig";
import { eid } from "../../utils/entityId";
import { MATCH_ADMIN_ROLES, normalizeUserRole } from "../../constants/roles";
import useEscapeKey from "../../hooks/useEscapeKey";
import "../../styles/features/matches.css";
import { eventTimeZone, formatEventDateTime } from "../../utils/eventTime";

const MatchDetails = ({ token, user, onLogout }) => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine where to go back based on referrer, restoring the active tab
  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from, {
        state: { restoreTab: location.state.tab }
      });
    } else {
      navigate(-1);
    }
  };
  
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editError, setEditError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  // Optional reason the host gives when cancelling (MT-B16)
  const [cancelReason, setCancelReason] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Score correction state
  const [showCorrectionForm, setShowCorrectionForm] = useState(false);
  const [correctionScore, setCorrectionScore] = useState({ participant1: 0, participant2: 0 });
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionLoading, setCorrectionLoading] = useState(false);
  const [correctionMsg, setCorrectionMsg] = useState(null);
  // Live countdown: seconds remaining in correction window
  const [correctionSecondsLeft, setCorrectionSecondsLeft] = useState(null);

  // Start-match validation modal state
  const [startValidation, setStartValidation] = useState(null); // { type, message, scheduledAt? }
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Escape closes the start-validation and cancel dialogs (BUG-44)
  useEscapeKey(!!startValidation, () => setStartValidation(null));
  useEscapeKey(showCancelConfirm && !actionLoading, () => setShowCancelConfirm(false));
  
  // Edit form state
  // The edit form as loaded: only a changed time / format is sent (MT-B19)
  const initialEditDataRef = useRef(null);
  const [editData, setEditData] = useState({
    match_name: "",
    venue: "",
    scheduled_at: "",
    notes: "",
    points_to_win: 21,
    games_to_win: 2,
    total_games: 3,
    win_by_margin: 2,
    max_points: 30
  });

  // Participant details state
  const [participant1Details, setParticipant1Details] = useState(null);
  const [participant2Details, setParticipant2Details] = useState(null);
  const [creatorDetails, setCreatorDetails] = useState(null);

  // Auto-open edit mode when navigated here with openEdit state (e.g. from MatchManagement)
  useEffect(() => {
    if (location.state?.openEdit && match && canEditMatch()) {
      setIsEditing(true);
      // Clear the state so a page refresh doesn't re-open edit
      window.history.replaceState({ ...window.history.state, state: { ...location.state, openEdit: false } }, '');
    }
  }, [match]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadMatchDetails();
  }, [matchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMatchDetails = async () => {
    try {
      setLoading(true);
      setError(null);


      const response = await matchService.getMatchById(matchId);
      
      if (response.success) {
        const matchData = response.data;
        setMatch(matchData);
        
        // Set edit form data - support both camelCase and snake_case
        const matchConfig = matchData.matchConfig || matchData.match_config || {};
        const scoringRules = matchConfig.scoring_rules || {};
        const gameRules = matchConfig.game_rules || {};
        
        // Helper: convert UTC ISO string from API to local datetime-local string
        const toDatetimeLocal = (iso) => {
          if (!iso) return "";
          const d = new Date(iso);
          // Shift back by timezone offset so the datetime-local input shows local time
          return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
        };

        const loadedEdit = {
          match_name: matchData.matchName || matchData.match_name || "",
          venue: matchData.venue || "",
          scheduled_at: toDatetimeLocal(matchData.scheduledAt || matchData.scheduled_at),
          notes: matchData.notes || "",
          points_to_win: scoringRules.points_to_win || 21,
          games_to_win: gameRules.games_to_win || 2,
          total_games: gameRules.total_games || 3,
          win_by_margin: scoringRules.win_by_margin || 2,
          max_points: scoringRules.max_points || 30
        };
        setEditData(loadedEdit);
        initialEditDataRef.current = loadedEdit;

        // Load participant details
        await loadParticipantDetails(matchData);
      } else {
        throw new Error(response.message || "Failed to load match");
      }
    } catch (err) {
      console.error("Error loading match:", err);
      setError(err.message || "Failed to load match details");
    } finally {
      setLoading(false);
    }
  };

  const loadParticipantDetails = async (matchData) => {
    try {
      // Load creator/host details - check if we have the data already from the match
      const creatorId = matchData.created_by || matchData.createdBy || matchData.creator_id || matchData.creatorId;
      
      // If we have creator details already in the match data, use them
      if (matchData.creatorFirstName || matchData.creator_first_name) {
        const creatorData = {
          id: creatorId,
          firstName: matchData.creatorFirstName || matchData.creator_first_name,
          first_name: matchData.creatorFirstName || matchData.creator_first_name,
          lastName: matchData.creatorLastName || matchData.creator_last_name,
          last_name: matchData.creatorLastName || matchData.creator_last_name,
          email: matchData.creatorEmail || matchData.creator_email
        };
        setCreatorDetails(creatorData);
      } else if (creatorId) {
        // Fallback: fetch creator details if not in match data
        try {
          const creatorResponse = await userService.getUserById(creatorId);
          
          if (creatorResponse.success) {
            setCreatorDetails(creatorResponse.data);
          }
        } catch (err) {
          console.error("❌ Error loading creator details:", err);
        }
      } else {
        console.warn('⚠️ No creator ID found in match data');
      }

      // Load participant 1 details
      if (matchData.participant_1_id) {
        if (matchData.participant_1_type === 'team') {
          const teamResponse = await teamService.getTeamById(matchData.participant_1_id);
          setParticipant1Details(teamResponse.data);
        } else {
          const userResponse = await userService.getUserById(matchData.participant_1_id);
          setParticipant1Details(userResponse.data);
        }
      }

      // Load participant 2 details
      if (matchData.participant_2_id) {
        if (matchData.participant_2_type === 'team') {
          const teamResponse = await teamService.getTeamById(matchData.participant_2_id);
          setParticipant2Details(teamResponse.data);
        } else {
          const userResponse = await userService.getUserById(matchData.participant_2_id);
          setParticipant2Details(userResponse.data);
        }
      }
    } catch (err) {
      console.error("Error loading participant details:", err);
    }
  };

  const handleEditToggle = () => {
    setEditError(null);
    setIsEditing(!isEditing);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      
      // Validate match_name is not empty
      if (!editData.match_name || editData.match_name.trim() === "") {
        setEditError("Match name is required");
        setActionLoading(false);
        return;
      }
      
      const initial = initialEditDataRef.current || {};
      const changed = (key) => String(editData[key] ?? "") !== String(initial[key] ?? "");
      const updateData = {
        match_name: editData.match_name.trim(),
        venue: editData.venue || null,
        notes: editData.notes || null,
      };
      // MT-B19: an unchanged (possibly already past) time is not re-sent, and
      // the format is only sent when the host changed it, so a volleyball
      // match is never overwritten with the form's badminton fallbacks.
      if (changed("scheduled_at")) {
        updateData.scheduled_at = editData.scheduled_at ? new Date(editData.scheduled_at).toISOString() : null;
      }
      if (["points_to_win", "win_by_margin", "max_points", "games_to_win", "total_games"].some(changed)) {
        updateData.match_config = {
          scoring_rules: {
            points_to_win: parseInt(editData.points_to_win),
            win_by_margin: parseInt(editData.win_by_margin),
            max_points: parseInt(editData.max_points)
          },
          game_rules: {
            games_to_win: parseInt(editData.games_to_win),
            total_games: parseInt(editData.total_games)
          }
        };
      }

      const response = await matchService.updateMatch(matchId, updateData);
      
      if (response.success) {
        // Reload match details to get updated data
        await loadMatchDetails();
        setIsEditing(false);
        setEditError(null);
      } else {
        throw new Error(response.message || "Failed to update match");
      }
    } catch (err) {
      console.error("Error updating match:", err);
      setEditError(err.message || "Failed to update match");
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartMatch = async () => {
    // Validation 1: friendly challenge must be accepted before starting
    const challengeStatus = match.challengeStatus || match.challenge_status;
    if (challengeStatus === 'declined') {
      setStartValidation({ type: 'challenge_declined' });
      return;
    }
    if (challengeStatus === 'pending') {
      setStartValidation({ type: 'pending_challenge' });
      return;
    }

    // Validation 2: cannot start more than 5 minutes before scheduled time
    const scheduledAt = match.scheduledAt || match.scheduled_at;
    if (scheduledAt) {
      const scheduledMs = new Date(scheduledAt).getTime();
      const nowMs = Date.now();
      const fiveMinMs = 5 * 60 * 1000;
      if (scheduledMs - nowMs > fiveMinMs) {
        setStartValidation({ type: 'too_early', scheduledAt });
        return;
      }
    }

    await doStartMatch();
  };

  const doStartMatch = async () => {
    try {
      setActionLoading(true);
      const response = await matchService.startMatch(matchId);
      if (response.success) {
        navigate(`/matches/${matchId}/live`, { state: location.state });
      } else {
        throw new Error(response.message || "Failed to start match");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || "Failed to start match";
      // Map backend challenge errors to the same popup dialogs
      if (msg.startsWith("Challenge pending")) {
        setStartValidation({ type: 'pending_challenge' });
      } else if (msg.startsWith("Challenge declined")) {
        setStartValidation({ type: 'challenge_declined' });
      } else {
        setError(msg);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendChallenge = async () => {
    try {
      setResendLoading(true);
      setResendSuccess(false);
      await matchService.resendChallenge(matchId);
      setResendSuccess(true);
    } catch (err) {
      console.error("Error resending challenge:", err);
      setError(err.message || "Failed to resend challenge");
    } finally {
      setResendLoading(false);
    }
  };

  const handleCancelMatch = async () => {
    try {
      setActionLoading(true);
      const reason = cancelReason.trim();
      await (reason ? matchService.cancelMatch(matchId, reason) : matchService.cancelMatch(matchId));
      setCancelReason('');
      await loadMatchDetails();
    } catch (err) {
      console.error("Error cancelling match:", err);
      setError(err.message || "Failed to cancel match");
    } finally {
      setActionLoading(false);
      setShowCancelConfirm(false);
    }
  };

  const handleSubmitScoreCorrection = async () => {
    try {
      setCorrectionLoading(true);
      setCorrectionMsg(null);
      const resp = await matchService.initiateScoreCorrection(matchId, {
        proposedScore: correctionScore,
        correctionReason,
      });
      setCorrectionMsg({ type: 'success', text: resp?.message || 'Score correction submitted. Both teams have 2 hours to respond.' });
      setShowCorrectionForm(false);
      await loadMatchDetails();
    } catch (err) {
      setCorrectionMsg({ type: 'error', text: err?.response?.data?.message || err.message || 'Failed to submit correction' });
    } finally {
      setCorrectionLoading(false);
    }
  };

  const handleVoteScoreCorrection = async (vote) => {
    try {
      setCorrectionLoading(true);
      setCorrectionMsg(null);
      const resp = await matchService.voteScoreCorrection(matchId, vote);
      const fallback = vote === 'accept' ? 'You accepted the corrected score.' : 'You rejected the correction. Original score retained if the other team also rejects.';
      setCorrectionMsg({ type: 'success', text: resp?.message || fallback });
      await loadMatchDetails();
    } catch (err) {
      setCorrectionMsg({ type: 'error', text: err?.response?.data?.message || err.message || 'Failed to record vote' });
    } finally {
      setCorrectionLoading(false);
    }
  };

  const handleFinalizeMatch = async () => {
    try {
      setCorrectionLoading(true);
      setCorrectionMsg(null);
      await matchService.finalizeMatch(matchId);
      setCorrectionMsg({ type: 'success', text: 'Match finalized. Score is permanently locked.' });
      await loadMatchDetails();
    } catch (err) {
      setCorrectionMsg({ type: 'error', text: err?.response?.data?.message || err.message || 'Failed to finalize match' });
    } finally {
      setCorrectionLoading(false);
    }
  };

  // Live countdown for the 2-hour correction window
  useEffect(() => {
    const deadline = match?.score_correction_deadline;
    if (!deadline || match?.score_correction_status !== 'pending_votes') {
      setCorrectionSecondsLeft(null);
      return;
    }
    const tick = () => {
      const left = Math.max(0, new Date(deadline).getTime() - Date.now());
      setCorrectionSecondsLeft(Math.floor(left / 1000));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [match?.score_correction_deadline, match?.score_correction_status]);

  // The challenged side of an accepted challenge withdraws before the start
  const handleWithdraw = async () => {
    if (!window.confirm('Withdraw from this match? It will be cancelled and the host will be told.')) return;
    try {
      setActionLoading(true);
      await matchService.withdrawMatch(matchId);
      await loadMatchDetails();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to withdraw from the match');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteMatch = async () => {
    try {
      setActionLoading(true);
      
      const response = await matchService.deleteMatch(matchId);
      
      if (response.success) {
        // Navigate back to wherever the user came from (league matches, tournament, etc.)
        handleBack();
      } else {
        throw new Error(response.message || "Failed to delete match");
      }
    } catch (err) {
      console.error("Error deleting match:", err);
      setError(err.message || "Failed to delete match");
    } finally {
      setActionLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  // At the venue's local time (utils/eventTime)
  const formatDateTime = (dateString) => {
    if (!dateString) return "Not scheduled";
    return formatEventDateTime(dateString, eventTimeZone(match));
  };

  const getParticipantName = (participant, type) => {
    if (!participant) return "Unknown";
    
    if (type === 'team') {
      return participant.name || "Unknown Team";
    } else {
      return participant.full_name || 
             `${participant.first_name || ''} ${participant.last_name || ''}`.trim() ||
             participant.username || 
             participant.email || 
             "Unknown User";
    }
  };

  // For drop-in matches the real teams are stored inside matchConfig.drop_in_teams
  // (participants are stored as the first player of each team for DB reasons).
  const getDropInTeamName = (side /* 'a' | 'b' */) => {
    const cfg = match?.matchConfig || match?.match_config;
    if (!cfg) return null;
    const teams = typeof cfg === 'string' ? (() => { try { return JSON.parse(cfg); } catch { return {}; } })() : cfg;
    const dt = teams?.drop_in_teams;
    if (!dt) return null;
    return side === 'a' ? (dt.team_a?.name || null) : (dt.team_b?.name || null);
  };

  // Resolved display name for each participant — prefers drop-in team name when present
  const getDisplayName = (side /* 1 | 2 */) => {
    const teamName = getDropInTeamName(side === 1 ? 'a' : 'b');
    if (teamName) return teamName;
    const pDetails = side === 1 ? participant1Details : participant2Details;
    const pType    = side === 1 ? (match?.participant_1_type || match?.participant1Type) : (match?.participant_2_type || match?.participant2Type);
    const pName    = side === 1 ? (match?.participant_1_name || match?.participant1Name) : (match?.participant_2_name || match?.participant2Name);
    return pName || getParticipantName(pDetails, pType);
  };


  // Helper function to get sport-specific defaults for display
  const getSportSpecificDefaults = () => {
    if (!match?.sport_name) return getSportDefaults('badminton'); // Fallback
    return getSportDefaults(match.sport_name);
  };

  // Helper function to format sport-specific rule display
  const formatRuleValue = (sportDefaults, ruleType, configValue = null) => {
    const defaultValue = sportDefaults[ruleType];

    // If the sport has no default for this field (e.g. badminton has no
    // total_games in its defaults), prefer the saved configValue.
    if (defaultValue === null || defaultValue === undefined) {
      if (configValue !== null && configValue !== undefined) return configValue;
      // Hard fallback when neither default nor saved value exists
      switch(ruleType) {
        case 'points_to_win': return 'No point limit (Timed game)';
        case 'max_points':    return 'No limit';
        case 'total_games':   return 1; // assume single game for timed sports
        default:              return 'Not applicable';
      }
    }

    // Use saved config when it differs from the default (i.e. was customised)
    if (configValue !== null && configValue !== undefined && configValue !== defaultValue) {
      return configValue;
    }

    return defaultValue;
  };

  // Permission helpers — backed by userPermissions from the API when available
  const canStart = () => {
    if (!user || !match) return false;
    if (match.userPermissions) return !!match.userPermissions.canStart;
    // Fallback: admins and host (same roles as the backend ADMIN_ROLES)
    if (MATCH_ADMIN_ROLES.includes(normalizeUserRole(user.role))) return true;
    const matchCreator = match.created_by || match.createdBy || match.creator_id || match.creatorId;
    return matchCreator != null && Number(matchCreator) === Number(user.id);
  };

  // canManageMatch: user has permission over this match (used for delete — any status)
  const canManageMatch = () => {
    if (!user || !match) return false;
    if (match.userPermissions) return !!match.userPermissions.canEdit;
    // Fallback: admins and host only (same roles as the backend ADMIN_ROLES)
    if (MATCH_ADMIN_ROLES.includes(normalizeUserRole(user.role))) return true;
    const matchCreator = match.created_by || match.createdBy || match.creator_id || match.creatorId;
    return matchCreator != null && Number(matchCreator) === Number(user.id);
  };

  // canDeleteMatch: a completed / finalized result is locked — only platform admins may erase it
  const canDeleteMatch = () => {
    if (!canManageMatch()) return false;
    const status = match?.status || match?.matchStatus;
    const resultLocked = status === 'completed' || !!(match?.finalized_at || match?.finalizedAt);
    if (!resultLocked) return true;
    return user.role === 'super_admin' || user.role === 'platform_admin';
  };

  // canEditMatch: edit is only allowed for scheduled matches
  const canEditMatch = () => {
    const status = match?.status || match?.matchStatus;
    return status === 'scheduled' && canManageMatch();
  };

  if (loading) {
    return (
      <GameLayout token={token} onLogout={onLogout} profile={user}>
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <h3>Loading match details...</h3>
        </div>
      </GameLayout>
    );
  }

  if (error) {
    return (
      <GameLayout token={token} onLogout={onLogout} profile={user}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "50vh" }}>
          <FloatingCard hover={false} style={{ background: "rgba(15,20,30,0.97)", maxWidth: "480px", width: "100%", textAlign: "center", padding: "2rem" }}>
            <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>🚫</div>
            <h2 style={{ color: "#f87171", marginBottom: "0.75rem", fontSize: "1.3rem" }}>Error</h2>
            <p style={{ color: "#94a3b8", marginBottom: "1.5rem", lineHeight: 1.6 }}>{error}</p>
            <div className="button-group">
              <FloatingButton onClick={handleBack} variant="secondary" size="medium" icon="←">Back</FloatingButton>
              <FloatingButton onClick={loadMatchDetails} variant="primary" size="medium" icon="🔄">Retry</FloatingButton>
            </div>
          </FloatingCard>
        </div>
      </GameLayout>
    );
  }

  if (!match) {
    return (
      <GameLayout token={token} onLogout={onLogout} profile={user}>
        <FloatingCard className="not-found-container">
          <h2>🔍 Match Not Found</h2>
          <p>The match you're looking for doesn't exist or has been deleted.</p>
          <FloatingButton 
            onClick={handleBack}
            className="primary"
          >
            ← Back
          </FloatingButton>
        </FloatingCard>
      </GameLayout>
    );
  }

  return (
    <GameLayout token={token} onLogout={onLogout} profile={user}>
      <div className="match-details-container">

        {/* ── Past schedule banner (scheduled but time has elapsed) ── */}
        {match.status === 'scheduled' && (() => {
          const schAt = match.scheduledAt || match.scheduled_at;
          return schAt && new Date(schAt).getTime() < Date.now();
        })() && (
          <div className="match-past-schedule-banner">
            <span className="banner-icon">⚠️</span>
            <div className="banner-body">
              <strong>This match was scheduled for {formatDateTime(match.scheduledAt || match.scheduled_at)} but has not started.</strong>
              <span className="banner-sub"> Please reschedule or cancel this match.</span>
            </div>
            <div className="banner-actions">
              {canEditMatch() && (
                <button
                  className="banner-btn banner-btn--reschedule"
                  onClick={() => setIsEditing(true)}
                >
                  📅 Reschedule
                </button>
              )}
              {canManageMatch() && (
                <button
                  className="banner-btn banner-btn--cancel"
                  onClick={() => setShowCancelConfirm(true)}
                  disabled={actionLoading}
                >
                  ❌ Cancel Match
                </button>
              )}
            </div>
          </div>
        )}

        {/* Match Header */}
        <FloatingCard className="match-header">
          <div className="match-title-section">
            <StatusBadge status={match.status} kind="match" />
            <h3>{(() => {
              const stored = match.matchName || match.match_name || `Match #${match.id}`;
              const p1 = match.participant_1_name || match.participant1Name;
              const p2 = match.participant_2_name || match.participant2Name;
              if (p1 && p2 && /Participant \d+/i.test(stored)) {
                const tn = match.tournamentName || match.tournament_name;
                return tn ? `${tn}: ${p1} vs ${p2}` : `${p1} vs ${p2}`;
              }
              return stored;
            })()}</h3>
            <p className="match-sport">{match.sport_name || `Sport ID: ${match.sport_id}`}</p>

            {/* Doubles partners (MT-B14) */}
            {(() => {
              const partners = getDoublesPartners(match);
              if (!partners) return null;
              const names = (list) => list.map((p) => p.name).join(' & ') || '—';
              return (
                <p className="match-doubles-partners" data-testid="doubles-partners" style={{ fontSize: '0.82rem', color: '#94a3b8' }}>
                  👥 Doubles: {names(partners.participant1)} vs {names(partners.participant2)}
                </p>
              );
            })()}

            {/* Cancellation details (MT-B16) */}
            {(() => {
              const info = getCancellationInfo(match);
              if (!info) return null;
              return (
                <div className="match-cancel-info" data-testid="cancel-info" style={{ fontSize: '0.82rem', color: '#f87171', marginTop: '0.4rem' }}>
                  ❌ Cancelled{info.at ? ` on ${new Date(info.at).toLocaleString()}` : ''}
                  {info.reason && <div>Reason: {info.reason}</div>}
                </div>
              );
            })()}
            
            {/* Winner Display for Completed Matches */}
            {match.status === 'completed' && (
              <div className="winner-display">
                {(match.finalizedAt || match.finalized_at) && (
                  <div style={{ marginBottom: '0.4rem', fontSize: '0.72rem', color: '#ef4444', fontWeight: 700, letterSpacing: '0.05em' }}>
                    🔒 SCORE FINALIZED
                  </div>
                )}
                {(match.resultType === 'forfeit' || match.result_type === 'forfeit') && (
                  <div style={{ marginBottom: '0.4rem', fontSize: '0.72rem', color: '#f59e0b', fontWeight: 700, letterSpacing: '0.05em' }}>
                    🏳️ WON BY FORFEIT
                  </div>
                )}
                {(match.winnerId || match.winner_id) ? (
                  <div className="winner-banner">
                    🏆 <strong>WINNER:</strong> {(() => {
                      const winnerId = match.winnerId || match.winner_id;
                      const participant1Id = match.participant1Id || match.participant_1_id;
                      const participant2Id = match.participant2Id || match.participant_2_id;
                      
                      if (winnerId === participant1Id) return getDisplayName(1);
                      if (winnerId === participant2Id) return getDisplayName(2);
                      return 'Unknown Winner';
                    })()}
                  </div>
                ) : (
                  <div className="winner-banner" style={{background: 'linear-gradient(135deg, #666 0%, #444 100%)'}}>
                    🤝 <strong>MATCH COMPLETED</strong> - Winner not recorded
                  </div>
                )}
                <div className="final-score">
                  Final Score: {(() => {
                    // Helper: extract a numeric total from a score value that may be
                    // a plain number OR an object like {total, periods, breakdown}
                    const getTotal = (v) => {
                      if (v == null) return 0;
                      if (typeof v === 'number') return v;
                      if (typeof v === 'object') {
                        const inner = v.total ?? v.score ?? v.value ?? 0;
                        return typeof inner === 'number' ? inner : 0;
                      }
                      return Number(v) || 0;
                    };

                    // Try scoreData first (from API), then score_data (raw)
                    if (match.scoreData && typeof match.scoreData === 'object') {
                      return `${getTotal(match.scoreData.participant1)} - ${getTotal(match.scoreData.participant2)}`;
                    }
                    if (typeof match.score_data === 'string') {
                      try {
                        const parsed = JSON.parse(match.score_data);
                        return `${getTotal(parsed.participant1)} - ${getTotal(parsed.participant2)}`;
                      } catch {
                        return '0 - 0';
                      }
                    }
                    return `${getTotal(match.score_data?.participant1)} - ${getTotal(match.score_data?.participant2)}`;
                  })()}
                </div>

                {/* Per-game breakdown (G1 21–15, G2 21–10, …) */}
                {(() => {
                  const rawScore = match.scoreData || match.score_data;
                  const parsed = typeof rawScore === 'string' ? (() => { try { return JSON.parse(rawScore); } catch { return null; } })() : rawScore;
                  const games = parsed?.gameScores;
                  if (!Array.isArray(games) || games.length === 0) return null;
                  const winnerId = match.winnerId || match.winner_id;
                  const p1Id = match.participant1Id || match.participant_1_id;
                  const p1Won = winnerId === p1Id;
                  return (
                    <div className="game-scores-breakdown">
                      <div className="game-scores-label">Games:</div>
                      <div className="game-scores-list">
                        {games.map((g, i) => {
                          const p1g = g.p1 ?? g.participant1 ?? 0;
                          const p2g = g.p2 ?? g.participant2 ?? 0;
                          const gameWon1 = p1g > p2g;
                          return (
                            <span
                              key={i}
                              className={`game-score-pill${gameWon1 ? (p1Won ? ' game-score-pill--winner' : '') : (!p1Won ? ' game-score-pill--winner' : '')}`}
                            >
                              <span className="game-score-pill__dot">●</span>
                              G{i + 1} {p1g}–{p2g}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="match-actions">
            <FloatingButton 
              onClick={handleBack}
              className="secondary"
            >
              ← Back
            </FloatingButton>
            
            {match.status === 'scheduled' && canStart() && (
              <FloatingButton 
                onClick={handleStartMatch}
                className="success"
                disabled={actionLoading}
              >
                ▶️ Start Match
              </FloatingButton>
            )}
            
            {match.status === 'in_progress' && (
              <FloatingButton 
                onClick={() => navigate(`/matches/${eid(match) || matchId}/live`)}
                className="success"
              >
                📺 View Live
              </FloatingButton>
            )}

            {canEditMatch() && (
              <FloatingButton 
                onClick={handleEditToggle}
                className={isEditing ? "secondary" : "primary"}
                disabled={actionLoading}
              >
                {isEditing ? "❌ Cancel" : "✏️ Edit"}
              </FloatingButton>
            )}

            {match.viewer?.canWithdraw && (
              <FloatingButton
                onClick={handleWithdraw}
                className="danger"
                disabled={actionLoading}
                data-testid="withdraw-match"
              >
                🏳️ Withdraw
              </FloatingButton>
            )}

            {canDeleteMatch() && (
              <FloatingButton
                onClick={() => setShowDeleteConfirm(true)}
                className="danger"
                disabled={actionLoading}
              >
                🗑️ Delete
              </FloatingButton>
            )}

            <FloatingButton
              onClick={() => setShowShareModal(true)}
              className="secondary"
            >
              🔗 Share
            </FloatingButton>
          </div>
        </FloatingCard>

        {/* Participants Section */}
        <FloatingCard className="participants-section">
          <h3>🏆 Participants</h3>
          {/* Drop-in team display — show team name + roster from match_config.drop_in_teams */}
          {(() => {
            const mc  = match.matchConfig || match.match_config || {};
            const dit = mc.drop_in_teams;
            if (!dit) return null;
            const teamA = dit.team_a || {};
            const teamB = dit.team_b || {};
            return (
              <div className="participants-grid">
                <div className="participant-card">
                  <ParticipantAvatar name={teamA.name || 'Team A'} color={teamA.color} kind="team" size="lg" className="participant-card__avatar" />
                  <div className="participant-header">
                    <span className="participant-label">⚽ Team A</span>
                  </div>
                  <div className="participant-name">{teamA.name || 'Team A'}</div>
                  {(teamA.players || []).length > 0 && (
                    <div style={{ marginTop: '0.5rem' }}>
                      {(teamA.players || []).map((p) => (
                        <div key={p.user_id} style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)', padding: '0.15rem 0' }}>
                          {p.display_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.username}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="vs-divider"><span className="vs-text">VS</span></div>
                <div className="participant-card">
                  <ParticipantAvatar name={teamB.name || 'Team B'} color={teamB.color} kind="team" size="lg" className="participant-card__avatar" />
                  <div className="participant-header">
                    <span className="participant-label">⚽ Team B</span>
                  </div>
                  <div className="participant-name">{teamB.name || 'Team B'}</div>
                  {(teamB.players || []).length > 0 && (
                    <div style={{ marginTop: '0.5rem' }}>
                      {(teamB.players || []).map((p) => (
                        <div key={p.user_id} style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)', padding: '0.15rem 0' }}>
                          {p.display_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.username}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          {/* Standard participant display — used for non drop-in matches */}
          {!((match.matchConfig || match.match_config || {}).drop_in_teams) && (
          <div className="participants-grid">
            <div className="participant-card">
              <ParticipantAvatar
                name={matchSides(match)[0].name}
                image={matchSides(match)[0].image}
                kind={matchSides(match)[0].kind}
                size="lg"
                className="participant-card__avatar"
              />
              <div className="participant-header">
                <span className="participant-label">
                  {(match.participant1Type || match.participant_1_type) === 'team' ? '🏟️ Team 1' : '👤 Player 1'}
                </span>
              </div>
              <div
                className={`participant-name${
                  (match.participant1Type || match.participant_1_type) === 'team'
                    ? ' participant-name--link'
                    : ' participant-name--link'
                }`}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  const p1Type = match.participant1Type || match.participant_1_type;
                  if (p1Type === 'team') {
                    navigate(`/teams/${match.participant1ShortCode || match.participant1Uuid || match.participant1Id || match.participant_1_id}`, {
                      state: { returnTo: 'browse', from: `/matches/${matchId}` },
                    });
                  } else {
                    const uid = participant1Details?.short_code || participant1Details?.uuid || match.participant1Id || match.participant_1_id;
                    if (uid) navigate(`/players/${uid}`);
                  }
                }}
              >
                {participant1Details ? (
                  (match.participant1Type || match.participant_1_type) === 'team' 
                    ? participant1Details.name || participant1Details.teamName || "Unknown Team"
                    : `${participant1Details.firstName || participant1Details.first_name || ''} ${participant1Details.lastName || participant1Details.last_name || ''}`.trim() || participant1Details.username || "Unknown Player"
                ) : (
                  match.participant_1_name || match.participant1Name || "Loading..."
                )}
              </div>
              <div className="participant-type">
                {match.participant1Type || match.participant_1_type}
              </div>
            </div>

            <div className="vs-divider">
              <span className="vs-text">VS</span>
            </div>

            <div className="participant-card">
              <ParticipantAvatar
                name={matchSides(match)[1].name}
                image={matchSides(match)[1].image}
                kind={matchSides(match)[1].kind}
                size="lg"
                className="participant-card__avatar"
              />
              <div className="participant-header">
                <span className="participant-label">
                  {(match.participant2Type || match.participant_2_type) === 'team' ? '🏟️ Team 2' : '👤 Player 2'}
                </span>
              </div>
              <div
                className={`participant-name${
                  (match.participant2Type || match.participant_2_type) === 'team'
                    ? ' participant-name--link'
                    : ' participant-name--link'
                }`}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  const p2Type = match.participant2Type || match.participant_2_type;
                  if (p2Type === 'team') {
                    navigate(`/teams/${match.participant2ShortCode || match.participant2Uuid || match.participant2Id || match.participant_2_id}`, {
                      state: { returnTo: 'browse', from: `/matches/${matchId}` },
                    });
                  } else {
                    const uid = participant2Details?.uuid || match.participant2Id || match.participant_2_id;
                    if (uid) navigate(`/players/${uid}`);
                  }
                }}
              >
                {participant2Details ? (
                  (match.participant2Type || match.participant_2_type) === 'team' 
                    ? participant2Details.name || participant2Details.teamName || "Unknown Team"
                    : `${participant2Details.firstName || participant2Details.first_name || ''} ${participant2Details.lastName || participant2Details.last_name || ''}`.trim() || participant2Details.username || "Unknown Player"
                ) : (
                  match.participant_2_name || match.participant2Name || "Loading..."
                )}
              </div>
              <div className="participant-type">
                {match.participant2Type || match.participant_2_type}
              </div>
            </div>
          </div>
          )}
        </FloatingCard>

        {/* Match Details Section */}
        <FloatingCard className="match-info-section">
          <h3>📋 Match Information</h3>
          
          {isEditing ? (
            <form onSubmit={handleEditSubmit} className="edit-form">
              {editError && (
                <div className="error-message" style={{ color: '#f87171', marginBottom: '1rem' }}>
                  ⚠️ {editError}
                </div>
              )}
              <div className="form-group-gaming">
                <label htmlFor="match_name" className="form-label-gaming">🏆 Match Name</label>
                <input
                  type="text"
                  id="match_name"
                  value={editData.match_name}
                  onChange={(e) => setEditData(prev => ({ ...prev, match_name: e.target.value }))}
                  className="form-input-gaming"
                  placeholder="Enter match name"
                  maxLength="255"
                />
              </div>

              <div className="form-group-gaming">
                <label htmlFor="venue" className="form-label-gaming">📍 Venue</label>
                <input
                  type="text"
                  id="venue"
                  value={editData.venue}
                  onChange={(e) => setEditData(prev => ({ ...prev, venue: e.target.value }))}
                  className="form-input-gaming"
                  placeholder="Enter venue"
                />
              </div>

              <div className="form-group-gaming">
                <label htmlFor="points_to_win" className="form-label-gaming">🎯 Points to Win (Per Game)</label>
                <select
                  id="points_to_win"
                  value={editData.points_to_win}
                  onChange={(e) => setEditData(prev => ({ ...prev, points_to_win: e.target.value }))}
                  className="form-select-gaming"
                >
                  {(getSportConfigByName(match?.sport_name)?.options?.points_to_win || [
                    { value: 11, label: '11 Points' },
                    { value: 15, label: '15 Points' },
                    { value: 21, label: '21 Points' },
                    { value: 25, label: '25 Points' },
                  ]).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group-gaming">
                <label htmlFor="total_games" className="form-label-gaming">🏆 Match Format</label>
                <select
                  id="total_games"
                  value={editData.total_games}
                  onChange={(e) => {
                    const total = parseInt(e.target.value);
                    setEditData(prev => ({ 
                      ...prev, 
                      total_games: total,
                      games_to_win: Math.ceil(total / 2)
                    }));
                  }}
                  className="form-select-gaming"
                >
                  {(getSportConfigByName(match?.sport_name)?.options?.total_games || [
                    { value: 1, label: 'Single Game' },
                    { value: 3, label: 'Best of 3' },
                    { value: 5, label: 'Best of 5' },
                  ]).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group-gaming">
                <label htmlFor="win_by_margin" className="form-label-gaming">📊 Win by Margin</label>
                <select
                  id="win_by_margin"
                  value={editData.win_by_margin}
                  onChange={(e) => setEditData(prev => ({ ...prev, win_by_margin: e.target.value }))}
                  className="form-select-gaming"
                >
                  {(getSportConfigByName(match?.sport_name)?.options?.win_by_margin || [
                    { value: 0, label: 'No Margin Required' },
                    { value: 1, label: 'Win by 1' },
                    { value: 2, label: 'Win by 2' },
                  ]).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group-gaming">
                <label htmlFor="max_points" className="form-label-gaming">🔝 Max Points (Deuce Limit)</label>
                <select
                  id="max_points"
                  value={editData.max_points}
                  onChange={(e) => setEditData(prev => ({ ...prev, max_points: e.target.value }))}
                  className="form-select-gaming"
                >
                  {(getSportConfigByName(match?.sport_name)?.options?.max_points || [
                    { value: 25, label: '25 Points' },
                    { value: 30, label: '30 Points' },
                    { value: 999, label: 'No Limit' },
                  ]).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group-gaming">
                <label htmlFor="scheduled_at" className="form-label-gaming">📅 Scheduled Time</label>
                <input
                  type="datetime-local"
                  id="scheduled_at"
                  value={editData.scheduled_at}
                  onChange={(e) => setEditData(prev => ({ ...prev, scheduled_at: e.target.value }))}
                  className="form-input-gaming"
                  min={(() => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); })()}
                />
              </div>

              <div className="form-group-gaming">
                <label htmlFor="notes" className="form-label-gaming">📝 Notes</label>
                <textarea
                  id="notes"
                  value={editData.notes}
                  onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                  className="form-textarea-gaming"
                  rows="3"
                  placeholder="Match notes or description"
                />
              </div>

              <div className="form-actions">
                <FloatingButton 
                  type="button"
                  className="secondary"
                  onClick={handleEditToggle}
                  disabled={actionLoading}
                >
                  ❌ Cancel
                </FloatingButton>
                <FloatingButton 
                  type="submit" 
                  className="success"
                  disabled={actionLoading}
                >
                  {actionLoading ? "💾 Saving..." : "💾 Save Changes"}
                </FloatingButton>
              </div>
            </form>
          ) : (
            <div className="match-details-grid">
              <div className="detail-item">
                <span className="detail-label">🏆 Match Name:</span>
                <span className="detail-value">{(() => {
                  const stored = match.matchName || match.match_name || 'Unnamed Match';
                  const p1 = match.participant_1_name || match.participant1Name;
                  const p2 = match.participant_2_name || match.participant2Name;
                  if (p1 && p2 && /Participant \d+/i.test(stored)) {
                    const tn = match.tournamentName || match.tournament_name;
                    return tn ? `${tn}: ${p1} vs ${p2}` : `${p1} vs ${p2}`;
                  }
                  return stored;
                })()}</span>
              </div>

              <div className="detail-item">
                <span className="detail-label">📍 Venue:</span>
                <span className="detail-value">{match.venue || "Not specified"}</span>
              </div>

              <div className="detail-item">
                <span className="detail-label">🏟️ Court / Field:</span>
                <span className="detail-value">
                  {match.court
                    ? match.court
                    : <span style={{ opacity: 0.45 }}>N/A</span>
                  }
                </span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">📅 Scheduled:</span>
                <span className="detail-value">{formatDateTime(match.scheduledAt || match.scheduled_at)}</span>
              </div>
              
              {creatorDetails && (
                <div className="detail-item">
                  <span className="detail-label">👑 Host:</span>
                  <span className="detail-value">
                    {creatorDetails.firstName || creatorDetails.first_name} {creatorDetails.lastName || creatorDetails.last_name}
                  </span>
                </div>
              )}
              
              <div className="detail-item">
                <span className="detail-label">🆔 Tournament:</span>
                <span className="detail-value">
                  {(match.tournamentId || match.tournament_id) ? (
                    <span
                      style={{ cursor: 'pointer', color: '#a78bfa', textDecoration: 'underline' }}
                      onClick={() => navigate(`/tournaments/${match.tournamentUuid || match.tournamentId || match.tournament_id}`)}
                    >
                      {match.tournamentName || match.tournament_name || `Tournament #${match.tournamentId || match.tournament_id}`}
                    </span>
                  ) : "Standalone Match"}
                </span>
              </div>

              {/* League context — extracted from match_config when not a top-level field */}
              {(() => {
                const leagueName = match.leagueName || match.league_name ||
                  match.matchConfig?.league_name || match.match_config?.league_name;
                const leagueId = match.leagueId || match.league_id;
                if (!leagueName && !leagueId) return null;
                return (
                  <div className="detail-item">
                    <span className="detail-label">🏆 League:</span>
                    <span className="detail-value">
                      {leagueName ? (
                        <span
                          style={{ cursor: leagueId ? 'pointer' : 'default', color: leagueId ? '#a78bfa' : 'inherit', textDecoration: leagueId ? 'underline' : 'none' }}
                          onClick={() => leagueId && navigate(`/leagues/${leagueId}`)}
                        >
                          {leagueName}
                        </span>
                      ) : `League #${leagueId}`}
                    </span>
                  </div>
                );
              })()}
              
              <div className="detail-item">
                <span className="detail-label">🎯 Sport:</span>
                <span className="detail-value">{match.sportName || match.sport_name || `Sport ID: ${match.sportId || match.sport_id}`}</span>
              </div>
              
              {match.notes && (
                <div className="detail-item full-width">
                  <span className="detail-label">📝 Notes:</span>
                  <span className="detail-value">{match.notes}</span>
                </div>
              )}
              
              <div className="detail-item">
                <span className="detail-label">📅 Created:</span>
                <span className="detail-value">{formatDateTime(match.createdAt || match.created_at)}</span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">🔄 Updated:</span>
                <span className="detail-value">{formatDateTime(match.updatedAt || match.updated_at)}</span>
              </div>
              
              {/* Match Rules Configuration */}
              {(match.matchConfig || match.match_config) && (() => {
                const matchConfig = match.matchConfig || match.match_config;
                const sportDefaults = getSportSpecificDefaults();
                
                // Use sport-specific defaults for display, showing saved config only if customized
                const pointsToWin = formatRuleValue(
                  sportDefaults, 
                  'points_to_win', 
                  matchConfig?.scoring_rules?.points_to_win
                );
                const totalGames = formatRuleValue(
                  sportDefaults, 
                  'total_games', 
                  matchConfig?.game_rules?.total_games
                );
                const winMargin = formatRuleValue(
                  sportDefaults, 
                  'win_by_margin', 
                  matchConfig?.scoring_rules?.win_by_margin
                );
                const maxPoints = formatRuleValue(
                  sportDefaults, 
                  'max_points', 
                  matchConfig?.scoring_rules?.max_points
                );
                
                return (
                  <>
                    <div className="detail-item full-width">
                      <span className="detail-label">⚙️ Match Rules:</span>
                    </div>
                    
                    <div className="detail-item">
                      <span className="detail-label">🎯 Points to Win:</span>
                      <span className="detail-value">
                        {typeof pointsToWin === 'number' ? `${pointsToWin} points` : pointsToWin}
                      </span>
                    </div>
                    
                    <div className="detail-item">
                      <span className="detail-label">🏆 Match Format:</span>
                      <span className="detail-value">
                        {typeof totalGames === 'number' && totalGames > 1 
                          ? `Best of ${totalGames}`
                          : 'Single game'}
                      </span>
                    </div>
                    
                    <div className="detail-item">
                      <span className="detail-label">📊 Win Margin:</span>
                      <span className="detail-value">
                        {typeof winMargin === 'number' && winMargin > 0 
                          ? (match.sport_name?.toLowerCase().includes('football') ? `${winMargin} Goal` : `Win by ${winMargin}`)
                          : 'No margin required'}
                      </span>
                    </div>
                    
                    <div className="detail-item">
                      <span className="detail-label">🔝 Max Points:</span>
                      <span className="detail-value">
                        {typeof maxPoints === 'number' && maxPoints !== 999 
                          ? `${maxPoints} points` 
                          : maxPoints}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </FloatingCard>

        {/* Score Section (if match has started) */}
        {match.status !== 'scheduled' && (
          <FloatingCard className="score-section">
            <h3>📊 Match Score</h3>

            {/* Set history tabs for volleyball (and other multi-set sports) */}
            {(() => {
              const isVB = match.sport_name?.toLowerCase().includes('volleyball');
              if (!isVB) return null;
              try {
                // Set scores come from the server's score_data, so every viewer
                // sees the same history (LS-B07) — not this browser's localStorage.
                const setScores = getServerSetScores(match);
                if (setScores.length === 0) return null;

                // Derive set winners for dot colour
                const mc = match?.matchConfig || match?.match_config || {};
                const gr = mc.game_rules || mc.scoring_rules || {};
                const ptsToWin  = gr.points_to_win ?? mc.points_to_win ?? 25;
                const winMargin = gr.win_by_margin ?? mc.win_by_margin ?? 2;
                const totalSets = gr.total_games   ?? mc.total_sets   ?? 5;
                const finalSetPts = totalSets >= 5 ? 15 : ptsToWin;

                const winner = (p1, p2, cap) => {
                  const mx = Math.max(p1, p2);
                  if (mx < cap) return null;
                  if (Math.abs(p1 - p2) < winMargin) return null;
                  return p1 > p2 ? 'p1' : 'p2';
                };

                return (
                  <div style={{ marginBottom: '1.2rem' }}>
                    <div style={{ textAlign: 'center', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Set Scores
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {setScores.map((ss, idx) => {
                        const setNum = idx + 1;
                        const cap = setNum === totalSets ? finalSetPts : ptsToWin;
                        const w = winner(ss.p1, ss.p2, cap);
                        const dotColor = w === 'p1' ? '#22c55e' : w === 'p2' ? '#ef4444' : '#94a3b8';
                        return (
                          <div key={setNum} style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem',
                            background: 'rgba(15,23,42,0.06)', border: '1px solid rgba(148,163,184,0.25)',
                            borderRadius: '0.6rem', padding: '0.45rem 0.75rem', minWidth: '68px',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: dotColor, display: 'inline-block', flexShrink: 0 }} />
                              <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>S{setNum}</span>
                            </div>
                            <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#e2e8f0' }}>
                              {+ss.p1 || 0} – {+ss.p2 || 0}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ borderTop: '1px solid rgba(148,163,184,0.2)', marginTop: '1rem' }} />
                  </div>
                );
              } catch { return null; }
            })()}

            <div className="score-display">
              {/* Helper defined once for both sides */}
              {(() => {
                // Recursively extract a numeric score from any shape: number, {total,...}, etc.
                const extractScore = (v) => {
                  if (v == null) return 0;
                  if (typeof v === 'number') return v;
                  if (typeof v === 'object') {
                    const inner = v.total ?? v.score ?? v.value ?? 0;
                    return typeof inner === 'number' ? inner : extractScore(inner);
                  }
                  return Number(v) || 0;
                };

                const getScoreFor = (key) => {
                  if (match.scoreData && typeof match.scoreData === 'object') {
                    return extractScore(match.scoreData[key]);
                  }
                  const raw = typeof match.score_data === 'string'
                    ? (() => { try { return JSON.parse(match.score_data); } catch { return {}; } })()
                    : (match.score_data || {});
                  return extractScore(raw[key]);
                };

                return (
                  <>
                    <div className="score-item">
                      <span className="score-participant">
                        {getDisplayName(1)}
                      </span>
                      <span className="score-value">{getScoreFor('participant1')}</span>
                    </div>
                    <div className="score-divider">-</div>
                    <div className="score-item">
                      <span className="score-participant">
                        {getDisplayName(2)}
                      </span>
                      <span className="score-value">{getScoreFor('participant2')}</span>
                    </div>
                  </>
                );
              })()}
            </div>
            
            {match.status === 'completed' && (match.winnerId || match.winner_id) && (
              <div className="winner-announcement">
                🏆 Winner: {(() => {
                  const winnerId = match.winnerId || match.winner_id;
                  const participant1Id = match.participant1Id || match.participant_1_id;
                  const participant2Id = match.participant2Id || match.participant_2_id;
                  
                  if (winnerId === participant1Id) return getDisplayName(1);
                  if (winnerId === participant2Id) return getDisplayName(2);
                  return 'Unknown Winner';
                })()}
              </div>
            )}
          </FloatingCard>
        )}

        {/* ── Score Correction Section (completed matches, 2-hour window) ── */}
        {match.status === 'completed' && (() => {
          const endedAt  = match.endedAt  || match.ended_at;
          const finalizedAt = match.finalizedAt || match.finalized_at;
          const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
          const withinWindow = !finalizedAt && endedAt &&
            (Date.now() - new Date(endedAt).getTime()) < TWO_HOURS_MS;
          const corrStatus   = match.score_correction_status;
          const corrProposed = match.score_correction_proposed;
          const corrDeadline = match.score_correction_deadline;
          const corrVotes    = match.score_correction_votes || {};
          const isHost       = canManageMatch();

          // Live countdown display
          const fmtCountdown = (secs) => {
            if (secs == null || secs <= 0) return 'Expired';
            const h = Math.floor(secs / 3600);
            const m = Math.floor((secs % 3600) / 60);
            const s = secs % 60;
            return h > 0
              ? `${h}h ${String(m).padStart(2,'0')}m ${String(s).padStart(2,'0')}s`
              : `${String(m).padStart(2,'0')}m ${String(s).padStart(2,'0')}s`;
          };

          // Per-participant vote resolution
          // The backend's viewer.side knows team captains; fall back to user ids
          const myVoteSide = getViewerVoteSide(match, user);
          const myVote     = myVoteSide ? corrVotes[myVoteSide] : null;

          if (finalizedAt || corrStatus === 'finalized') {
            return (
              <FloatingCard className="match-info-section" style={{ marginTop: '1rem' }}>
                <h3>🔒 Score Finalized</h3>
                <p style={{ fontSize: '0.83rem', color: '#94a3b8' }}>
                  The match score has been permanently locked by the host. No further corrections are permitted.
                </p>
                <p style={{ fontSize: '0.75rem', color: '#475569' }}>
                  Finalized at: {new Date(finalizedAt).toLocaleString()}
                </p>
              </FloatingCard>
            );
          }

          return (
            <FloatingCard className="match-info-section" style={{ marginTop: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                <h3 style={{ margin: 0 }}>🔄 Score Correction</h3>

                {/* Live countdown banner for pending vote */}
                {corrStatus === 'pending_votes' && correctionSecondsLeft != null && (
                  <div style={{
                    padding: '0.3rem 0.8rem', borderRadius: '0.4rem',
                    background: correctionSecondsLeft > 600 ? 'rgba(99,102,241,0.2)' : 'rgba(239,68,68,0.18)',
                    border: `1px solid ${correctionSecondsLeft > 600 ? 'rgba(99,102,241,0.5)' : 'rgba(239,68,68,0.5)'}`,
                    color: correctionSecondsLeft > 600 ? '#a78bfa' : '#f87171',
                    fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.02em',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    ⏱ {fmtCountdown(correctionSecondsLeft)}
                  </div>
                )}
              </div>

              {/* Message banner */}
              {correctionMsg && (
                <div style={{
                  padding: '0.6rem 1rem', borderRadius: '0.4rem', marginBottom: '0.75rem', marginTop: '0.6rem',
                  background: correctionMsg.type === 'success' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                  border: `1px solid ${correctionMsg.type === 'success' ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)'}`,
                  color: correctionMsg.type === 'success' ? '#22c55e' : '#f87171',
                  fontSize: '0.85rem',
                }}>
                  {correctionMsg.text}
                </div>
              )}

              {/* Host only: Finalize or Request Correction */}
              {isHost && !corrStatus && (
                <div style={{ marginTop: '0.6rem' }}>
                  {withinWindow ? (
                    <>
                      <p style={{ fontSize: '0.83rem', color: '#94a3b8', marginBottom: '0.6rem' }}>
                        As host you can propose a score correction (both teams must vote within 2 hours),
                        or permanently lock the score now.
                      </p>
                      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => {
                            const rawScore = match.scoreData || match.score_data;
                            const parsed = typeof rawScore === 'string'
                              ? (() => { try { return JSON.parse(rawScore); } catch { return {}; } })()
                              : (rawScore || {});
                            setCorrectionScore({ participant1: parsed.participant1 || 0, participant2: parsed.participant2 || 0 });
                            setShowCorrectionForm(true);
                          }}
                          style={{ padding: '0.45rem 1rem', background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.5)', color: '#a78bfa', borderRadius: '0.4rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}
                        >
                          ✏️ Request Score Correction
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm('Permanently finalize this score? This cannot be undone and will prevent any future corrections.')) {
                              handleFinalizeMatch();
                            }
                          }}
                          disabled={correctionLoading}
                          style={{ padding: '0.45rem 1rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171', borderRadius: '0.4rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}
                        >
                          🔒 Finalize Score (No Undo)
                        </button>
                      </div>

                      {showCorrectionForm && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', marginTop: '0.75rem', padding: '0.75rem', background: 'rgba(0,0,0,0.2)', borderRadius: '0.4rem' }}>
                          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <label style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{getDisplayName(1)}</label>
                            <input type="number" min="0" value={correctionScore.participant1}
                              onChange={e => setCorrectionScore(prev => ({ ...prev, participant1: parseInt(e.target.value) || 0 }))}
                              style={{ width: '70px', padding: '0.35rem', background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', borderRadius: '0.3rem' }}
                            />
                            <span style={{ color: '#64748b' }}>–</span>
                            <input type="number" min="0" value={correctionScore.participant2}
                              onChange={e => setCorrectionScore(prev => ({ ...prev, participant2: parseInt(e.target.value) || 0 }))}
                              style={{ width: '70px', padding: '0.35rem', background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', borderRadius: '0.3rem' }}
                            />
                            <label style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{getDisplayName(2)}</label>
                          </div>
                          <input type="text" placeholder="Reason for correction (optional)" value={correctionReason}
                            onChange={e => setCorrectionReason(e.target.value)}
                            style={{ padding: '0.4rem 0.75rem', background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', borderRadius: '0.3rem', fontSize: '0.82rem' }}
                          />
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button onClick={handleSubmitScoreCorrection} disabled={correctionLoading}
                              style={{ padding: '0.4rem 1rem', background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.5)', color: '#22c55e', borderRadius: '0.35rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
                              {correctionLoading ? 'Submitting…' : '✔ Submit Correction'}
                            </button>
                            <button onClick={() => setShowCorrectionForm(false)}
                              style={{ padding: '0.4rem 0.9rem', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: '0.35rem', cursor: 'pointer', fontSize: '0.82rem' }}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div>
                      <p style={{ fontSize: '0.82rem', color: '#475569', marginBottom: '0.5rem' }}>
                        The 2-hour correction window has expired. You can still permanently finalize the score.
                      </p>
                      <button
                        onClick={() => {
                          if (window.confirm('Permanently finalize this score? This cannot be undone.')) {
                            handleFinalizeMatch();
                          }
                        }}
                        disabled={correctionLoading}
                        style={{ padding: '0.45rem 1rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171', borderRadius: '0.4rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}
                      >
                        🔒 Finalize Score
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Pending correction — vote UI (both host and participants see this) */}
              {corrStatus === 'pending_votes' && corrProposed && (
                <div style={{ marginTop: '0.6rem' }}>
                  <p style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                    A score correction has been proposed. Both teams must respond before the timer expires.
                  </p>
                  <div style={{ marginBottom: '0.6rem', fontSize: '0.85rem', color: '#e2e8f0' }}>
                    Proposed: <strong>{corrProposed.score?.participant1 ?? '?'} – {corrProposed.score?.participant2 ?? '?'}</strong>
                    {corrProposed.reason && <span style={{ color: '#64748b', marginLeft: '0.5rem' }}>({corrProposed.reason})</span>}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '0.75rem' }}>
                    {getDisplayName(1)}: <strong>{corrVotes.participant1 ?? 'pending'}</strong>
                    &nbsp;|&nbsp;
                    {getDisplayName(2)}: <strong>{corrVotes.participant2 ?? 'pending'}</strong>
                  </div>
                  {myVoteSide && !myVote && (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button onClick={() => handleVoteScoreCorrection('accept')} disabled={correctionLoading}
                        style={{ padding: '0.4rem 1rem', background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.5)', color: '#22c55e', borderRadius: '0.35rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
                        ✅ Accept New Score
                      </button>
                      <button onClick={() => handleVoteScoreCorrection('reject')} disabled={correctionLoading}
                        style={{ padding: '0.4rem 1rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171', borderRadius: '0.35rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
                        ❌ Reject Correction
                      </button>
                    </div>
                  )}
                  {myVote && (
                    <p style={{ fontSize: '0.82rem', color: '#64748b' }}>
                      Your vote: <strong style={{ color: myVote === 'accept' ? '#22c55e' : '#f87171' }}>{myVote}</strong>
                    </p>
                  )}
                  {/* Finalizing is blocked until the correction is settled (MT-B11) */}
                  {isHost && !canFinalizeNow(match) && (
                    <p data-testid="finalize-blocked" style={{ marginTop: '0.75rem', fontSize: '0.78rem', color: '#64748b' }}>
                      🔒 The score can be finalized once this correction is accepted, rejected or its deadline passes.
                    </p>
                  )}
                </div>
              )}

              {corrStatus === 'accepted'    && <p style={{ fontSize: '0.82rem', color: '#22c55e', marginTop: '0.5rem' }}>✅ Both teams accepted — corrected scores applied.</p>}
              {corrStatus === 'rejected'    && <p style={{ fontSize: '0.82rem', color: '#f87171', marginTop: '0.5rem' }}>❌ Correction rejected — original scores retained.</p>}
              {corrStatus === 'auto_applied'&& <p style={{ fontSize: '0.82rem', color: '#a78bfa', marginTop: '0.5rem' }}>🕒 Correction window expired — corrected scores auto-applied.</p>}
            </FloatingCard>
          );
        })()}

        {/* ── Result agreement: an expired match, or a result waiting for the other side ── */}
        {match.status !== 'completed' && (() => {
          const viewer = match.viewer || {};
          const pending = viewer.pendingResult;
          const proposal = match.score_correction_proposed;
          const declined = !pending && match.score_correction_status === 'rejected' && proposal?.kind === 'result';
          if (match.status !== 'expired' && !pending && !declined) return null;
          const sideName = (side) => (side === 'participant1' ? getDisplayName(1) : side === 'participant2' ? getDisplayName(2) : 'both sides');
          const score = pending?.score || proposal?.score;
          return (
            <FloatingCard className="match-info-section" style={{ marginTop: '1rem' }} data-testid="result-agreement">
              <h3 style={{ margin: 0 }}>{match.status === 'expired' ? '⌛ Expired, no result' : '📝 Result waiting for confirmation'}</h3>
              {match.status === 'expired' && (
                <p style={{ fontSize: '0.83rem', color: '#94a3b8', marginTop: '0.5rem' }}>
                  This match was left open, so it expired with no result and its players are free for their next games.
                  {(match.tournament_id || match.tournamentId || match.league_id || match.leagueId)
                    ? ' The league host or tournament organizer can still enter its result.'
                    : ' A result can still be added once both sides agree on it.'}
                </p>
              )}
              {correctionMsg && (
                <div style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: correctionMsg.type === 'success' ? '#22c55e' : '#f87171' }}>
                  {correctionMsg.text}
                </div>
              )}
              {pending && score && (
                <div style={{ marginTop: '0.6rem', fontSize: '0.85rem', color: '#e2e8f0' }}>
                  Proposed result: <strong>{getDisplayName(1)} {score.participant1 ?? '?'} – {score.participant2 ?? '?'} {getDisplayName(2)}</strong>
                </div>
              )}
              {pending && pending.isProposer && !viewer.canVote && (
                <p data-testid="waiting-confirm" style={{ fontSize: '0.83rem', color: '#a78bfa', marginTop: '0.5rem' }}>
                  ⏳ Waiting for {sideName(pending.awaitingSide)} to confirm
                </p>
              )}
              {pending && viewer.canVote && (
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
                  <button onClick={() => handleVoteScoreCorrection('accept')} disabled={correctionLoading}
                    style={{ padding: '0.4rem 1rem', background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.5)', color: '#22c55e', borderRadius: '0.35rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
                    ✅ Accept
                  </button>
                  <button onClick={() => handleVoteScoreCorrection('reject')} disabled={correctionLoading}
                    style={{ padding: '0.4rem 1rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171', borderRadius: '0.35rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
                    ❌ Decline
                  </button>
                </div>
              )}
              {pending && !pending.isProposer && !viewer.canVote && (
                <p style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '0.5rem' }}>
                  Waiting for {sideName(pending.awaitingSide)} to confirm.
                </p>
              )}
              {declined && (
                <p style={{ fontSize: '0.82rem', color: '#f87171', marginTop: '0.5rem' }}>
                  ❌ The other side declined the proposed result: the match stays open for a corrected one.
                </p>
              )}
              {viewer.canProposeResult && (
                <div style={{ marginTop: '0.6rem' }}>
                  {!showCorrectionForm ? (
                    <button
                      onClick={() => { setCorrectionScore({ participant1: 0, participant2: 0 }); setShowCorrectionForm(true); }}
                      style={{ padding: '0.45rem 1rem', background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.5)', color: '#a78bfa', borderRadius: '0.4rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}
                    >
                      ✏️ {viewer.isAdmin || (match.tournament_id || match.tournamentId || match.league_id || match.leagueId) ? 'Enter result' : 'Propose final result'}
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <label style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{getDisplayName(1)}</label>
                      <input type="number" min="0" aria-label="Participant 1 score" value={correctionScore.participant1}
                        onChange={e => setCorrectionScore(prev => ({ ...prev, participant1: parseInt(e.target.value) || 0 }))}
                        style={{ width: '70px', padding: '0.35rem', background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', borderRadius: '0.3rem' }}
                      />
                      <span style={{ color: '#64748b' }}>–</span>
                      <input type="number" min="0" aria-label="Participant 2 score" value={correctionScore.participant2}
                        onChange={e => setCorrectionScore(prev => ({ ...prev, participant2: parseInt(e.target.value) || 0 }))}
                        style={{ width: '70px', padding: '0.35rem', background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', borderRadius: '0.3rem' }}
                      />
                      <label style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{getDisplayName(2)}</label>
                      <button onClick={handleSubmitScoreCorrection} disabled={correctionLoading}
                        style={{ padding: '0.4rem 1rem', background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.5)', color: '#22c55e', borderRadius: '0.35rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
                        {correctionLoading ? 'Sending…' : '✔ Send result'}
                      </button>
                      <button onClick={() => setShowCorrectionForm(false)}
                        style={{ padding: '0.4rem 0.9rem', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: '0.35rem', cursor: 'pointer', fontSize: '0.82rem' }}>
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )}
            </FloatingCard>
          );
        })()}

        {/* Share Match Modal */}
        <ShareMatchModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          matchId={eid(match) || matchId}
          matchTitle={match?.matchName || match?.match_name || null}
        />

        {/* Cancel Confirmation Modal */}
        {showCancelConfirm && (
          <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="cancel-match-title">
            <FloatingCard className="confirm-modal">
              <h3 id="cancel-match-title">❌ Cancel Match</h3>
              <p>Are you sure you want to cancel this match? This cannot be undone.</p>
              <label htmlFor="cancel-match-reason" style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.3rem' }}>
                Reason (optional, shared with both sides)
              </label>
              <textarea
                id="cancel-match-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="e.g. Venue unavailable"
                style={{ width: '100%', marginBottom: '0.75rem', padding: '0.5rem', background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', borderRadius: '0.35rem' }}
              />
              <div className="modal-actions">
                <FloatingButton
                  onClick={() => setShowCancelConfirm(false)}
                  className="secondary"
                  disabled={actionLoading}
                >
                  Keep Match
                </FloatingButton>
                <FloatingButton
                  onClick={handleCancelMatch}
                  className="danger"
                  disabled={actionLoading}
                >
                  {actionLoading ? "Cancelling..." : "Yes, Cancel Match"}
                </FloatingButton>
              </div>
            </FloatingCard>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="modal-overlay">
            <FloatingCard className="confirm-modal">
              <h3>🗑️ Delete Match</h3>
              <p>Are you sure you want to delete this match? This action cannot be undone.</p>
              
              <div className="modal-actions">
                <FloatingButton 
                  onClick={() => setShowDeleteConfirm(false)}
                  className="secondary"
                  disabled={actionLoading}
                >
                  Cancel
                </FloatingButton>
                <FloatingButton 
                  onClick={handleDeleteMatch}
                  className="danger"
                  disabled={actionLoading}
                >
                  {actionLoading ? "Deleting..." : "Delete Match"}
                </FloatingButton>
              </div>
            </FloatingCard>
          </div>
        )}

        {/* Start Match Validation Modal */}
        {startValidation && (
          <div
            className="modal-overlay start-validation-overlay"
            role="dialog"
            aria-modal="true"
            aria-label={{
              challenge_declined: "Challenge declined",
              pending_challenge: "Challenge not accepted yet",
              too_early: "Too early to start",
            }[startValidation.type] || "Start match"}
          >
            <FloatingCard className="confirm-modal start-validation-modal">
              {startValidation.type === 'challenge_declined' && (
                <>
                  <div className="start-validation-icon">❌</div>
                  <h3>Challenge Declined</h3>
                  <p>
                    Your opponent declined this match challenge. The match has been cancelled.
                    You can create a new match if you'd like to challenge them again.
                  </p>
                  <div className="modal-actions">
                    <FloatingButton
                      onClick={() => setStartValidation(null)}
                      className="secondary"
                      aria-label="OK, close dialog"
                    >
                      OK
                    </FloatingButton>
                  </div>
                </>
              )}

              {startValidation.type === 'pending_challenge' && (
                <>
                  <div className="start-validation-icon">⚠️</div>
                  <h3>Opponent Hasn't Accepted</h3>
                  <p>
                    The challenged team has not yet accepted your challenge request.
                    You can send them a reminder or start the match anyway.
                  </p>
                  {resendSuccess && (
                    <div className="start-validation-success">
                      ✅ Reminder sent! The other team has been notified again.
                    </div>
                  )}
                  <div className="modal-actions">
                    <FloatingButton
                      onClick={() => { setStartValidation(null); setResendSuccess(false); }}
                      className="secondary"
                    >
                      Cancel
                    </FloatingButton>
                    <FloatingButton
                      onClick={handleResendChallenge}
                      className="primary"
                      disabled={resendLoading || resendSuccess}
                    >
                      {resendLoading ? "Sending…" : resendSuccess ? "✅ Sent" : "📨 Send Reminder"}
                    </FloatingButton>
                    <FloatingButton
                      onClick={() => { setStartValidation(null); doStartMatch(); }}
                      className="warning"
                      disabled={actionLoading}
                    >
                      ▶️ Start Anyway
                    </FloatingButton>
                  </div>
                </>
              )}

              {startValidation.type === 'too_early' && (
                <>
                  <div className="start-validation-icon">🕐</div>
                  <h3>Too Early to Start</h3>
                  <p>
                    This match is scheduled for{' '}
                    <strong>{formatDateTime(startValidation.scheduledAt)}</strong>.
                    You can only start a match within 5 minutes of the scheduled time.
                  </p>
                  <div className="modal-actions">
                    <FloatingButton
                      onClick={() => setStartValidation(null)}
                      className="secondary"
                      aria-label="OK, close dialog"
                    >
                      OK
                    </FloatingButton>
                  </div>
                </>
              )}
            </FloatingCard>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

export default MatchDetails;